import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import * as stylex from '@stylexjs/stylex'
import debounce from 'debounce'
import { Children, cloneElement, isValidElement, useEffect, useRef, useState, type ForwardedRef, type PropsWithChildren } from 'react'
import { type TabProps } from '../Tab/Tab'
import { useTabContext, type TabColor, type TabValue } from '../TabContext/TabContext'
import { TabsIndicator } from './TabsIndicator'

const baseStyles = stylex.create({
  root: {
    display: 'flex',
  },
  scroller: {
    'flex': '1 1 auto',
    'display': 'inline-block',
    'position': 'relative',
    'scrollbarWidth': 'none',
    'whiteSpace': 'nowrap',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  scrollerVertical: {
    whiteSpace: 'normal',
  },
  container: {
    display: 'flex',
  },
  containerVertical: {
    flexDirection: 'column',
  },
})

export interface TabListProps<Value extends TabValue = TabValue> {
  /** 目前選取的標籤值 */
  value?: Value
  /** 根元素的 ref */
  rootRef?: ForwardedRef<HTMLDivElement>
  /** 根元素的自訂樣式 */
  rootStyle?: stylex.StyleXStyles
  /**
   * 標籤頁顏色主題
   * @default 'primary'
   */
  color?: TabColor
  /**
   * 排列模式：standard 為內容寬度、fullWidth 為等寬撐滿
   * @default 'standard'
   */
  variant?: 'standard' | 'fullWidth'
  /** 標籤頁切換時的回呼 */
  onChange?: TabProps<Value>['onChange']
  /**
   * 標籤頁尺寸
   * @default 'medium'
   */
  size?: 'small' | 'medium'
  /**
   * 排列方向：horizontal 為橫向、vertical 為縱向
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical'
}

/**
 * TabList 標籤列容器，管理 Tab 子元件的選取狀態與底部指示器動畫。
 * 需搭配 TabContext 使用以實現受控切換。
 */
export const TabList = <Value extends TabValue>(props: PropsWithChildren<TabListProps<Value>>) => {
  const {
    rootRef,
    rootStyle,
    value: valueProp,
    variant = 'standard',
    color = 'primary',
    onChange,
    children,
    size = 'medium',
    orientation = 'horizontal',
    ...rest
  } = props
  const { value: contextValue } = useTabContext()
  const value = valueProp ?? contextValue
  const scrollerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [indicatorStyle, setIndicatorStyle] = useState({ start: 0, size: 0 })
  const [mounted, setMounted] = useState(false)
  const valueToIndex = new Map()
  const isVertical = orientation === 'vertical'

  const getTabsMeta = () => {
    const tabsNode = scrollerRef.current
    let tabsMeta
    if (tabsNode) {
      const rect = tabsNode.getBoundingClientRect()
      tabsMeta = {
        clientWidth: tabsNode.clientWidth,
        clientHeight: tabsNode.clientHeight,
        scrollLeft: tabsNode.scrollLeft,
        scrollTop: tabsNode.scrollTop,
        scrollWidth: tabsNode.scrollWidth,
        scrollHeight: tabsNode.scrollHeight,
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      }
    }

    let tabMeta
    if (tabsNode && containerRef.current) {
      const children = containerRef.current.children

      if (children.length > 0) {
        const tab = children[valueToIndex.get(value)]
        tabMeta = tab ? tab.getBoundingClientRect() : null
      }
    }
    return { tabsMeta, tabMeta }
  }

  const updateIndicatorState = useCallbackEvent(() => {
    const { tabsMeta, tabMeta } = getTabsMeta()
    const startKey = isVertical ? 'top' : 'left'
    const sizeKey = isVertical ? 'height' : 'width'
    const scrollKey = isVertical ? 'scrollTop' : 'scrollLeft'
    let startValue = 0

    if (tabMeta && tabsMeta) {
      startValue = tabMeta[startKey] - tabsMeta[startKey] + tabsMeta[scrollKey]
    }

    const newIndicatorStyle = {
      start: startValue,
      // May be wrong until the font is loaded.
      size: tabMeta ? tabMeta[sizeKey] : 0,
    }

    const dStart = Math.abs(indicatorStyle.start - newIndicatorStyle.start)
    const dSize = Math.abs(indicatorStyle.size - newIndicatorStyle.size)

    if (dStart >= 1 || dSize >= 1) {
      setIndicatorStyle(newIndicatorStyle)
    }
  })

  const indicator = (<TabsIndicator color={color} orientation={orientation} {...indicatorStyle} />)

  const tabs = Children.map(children, (child, childIndex) => {
    if (!isValidElement<TabProps<Value>>(child)) {
      return null
    }

    const childValue = child.props.value === undefined ? childIndex as Value : child.props.value
    valueToIndex.set(childValue, childIndex)
    const selected = childValue === value
    const divider = isVertical ? false : child.props.divider

    return cloneElement(child, {
      fullWidth: variant === 'fullWidth',
      indicator: selected && !mounted && indicator,
      selected,
      color,
      onChange,
      value: childValue,
      size,
      divider,
    })
  })

  useEffect(() => {
    setMounted(true)
  }, [])

  /**
   * Update indicator state on value change and tabs length change
   */
  useEffect(() => {
    updateIndicatorState()
  }, [value, tabs?.length, isVertical, updateIndicatorState])

  useEffect(() => {
    const handleResize = debounce(updateIndicatorState)
    updateIndicatorState()
    window.addEventListener('resize', handleResize)
    return () => {
      handleResize.clear()
      window.removeEventListener('resize', handleResize)
    }
  }, [updateIndicatorState])

  return (
    <div ref={rootRef} {...stylex.props(baseStyles.root, rootStyle)} {...rest}>
      <div ref={scrollerRef} {...stylex.props(baseStyles.scroller, isVertical && baseStyles.scrollerVertical)}>
        <div ref={containerRef} {...stylex.props(baseStyles.container, isVertical && baseStyles.containerVertical)}>
          {tabs}
        </div>
        {mounted && indicator}
      </div>
    </div>
  )
}
